import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
  TextField,
} from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { apiService, ApiError } from '../../services/apiService';

/**
 * Confirms a user's email address. Reads the one-time token from the URL query
 * (`/verify-email?token=...`) and calls the backend verify endpoint on mount.
 * On failure, offers a resend form so users are not stuck without a new link.
 */
const VerifyEmailForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>(
    token ? 'verifying' : 'error'
  );
  const [message, setMessage] = useState<string>(
    token ? '' : 'This verification link is missing its token. You can request a new one below.'
  );
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendFeedback, setResendFeedback] = useState<{
    severity: 'success' | 'error';
    text: string;
  } | null>(null);
  // Guard against double-invocation in React 18 StrictMode (dev).
  const startedRef = useRef(false);

  useEffect(() => {
    if (!token || startedRef.current) return;
    startedRef.current = true;
    (async () => {
      try {
        const res = await apiService.verifyEmail(token);
        const verified = res.data?.verified ?? res.data?.success ?? false;
        const ok = Boolean(res.success && verified);
        if (ok) {
          setStatus('success');
          setMessage(res.data?.message || 'Your email address has been verified.');
        } else {
          setStatus('error');
          setMessage(res.data?.message || 'This verification link is invalid or has expired.');
        }
      } catch (err) {
        setStatus('error');
        setMessage(
          err instanceof ApiError
            ? (err.status === 400
                ? 'This verification link is invalid or has expired. Request a new one below.'
                : err.message)
            : 'Something went wrong while verifying your email. Please try again.'
        );
      }
    })();
  }, [token]);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = resendEmail.trim();
    if (!email) {
      setResendFeedback({ severity: 'error', text: 'Enter the email address for your account.' });
      return;
    }
    setResending(true);
    setResendFeedback(null);
    try {
      const res = await apiService.resendVerificationEmail(email);
      setResendFeedback({
        severity: 'success',
        text:
          res.data?.message ||
          'If an unverified account exists for that email, a new verification link has been sent.',
      });
    } catch (err) {
      setResendFeedback({
        severity: 'error',
        text:
          err instanceof ApiError
            ? err.message
            : 'Could not send a verification email. Please try again shortly.',
      });
    } finally {
      setResending(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography component="h1" variant="h4" gutterBottom>
              Verify your email
            </Typography>
          </Box>

          {status === 'verifying' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Verifying your email address…
              </Typography>
            </Box>
          )}

          {status === 'success' && (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                {message}
              </Alert>
              <Button fullWidth variant="contained" component={RouterLink} to="/login">
                Continue to sign in
              </Button>
            </Box>
          )}

          {status === 'error' && (
            <Box>
              <Alert severity="error" sx={{ mb: 2 }}>
                {message}
              </Alert>

              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Request a new verification link
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the email you registered with. We’ll send a fresh link if the account
                still needs verification.
              </Typography>
              <Box component="form" onSubmit={handleResend} noValidate>
                <TextField
                  fullWidth
                  required
                  type="email"
                  label="Email address"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  disabled={resending}
                  autoComplete="email"
                  sx={{ mb: 2 }}
                />
                {resendFeedback && (
                  <Alert severity={resendFeedback.severity} sx={{ mb: 2 }}>
                    {resendFeedback.text}
                  </Alert>
                )}
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={resending}
                  sx={{ mb: 1 }}
                >
                  {resending ? <CircularProgress size={22} color="inherit" /> : 'Send verification email'}
                </Button>
              </Box>
              <Button fullWidth variant="text" component={RouterLink} to="/login">
                Back to sign in
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default VerifyEmailForm;
